import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

import {
  DgaNotificationToast,
} from "../PlatformsCode/PlatformsCode";

import "./DgaNotification.css";

const DGA_NOTIFICATION_EVENT =
  "ets:dga-notification";

/*
 * All notifications automatically close
 * after 5 seconds.
 */
const AUTO_DISMISS_DURATION = 5000;

const EXIT_ANIMATION_DURATION = 320;

let notificationSequence = 0;

const createNotificationId = () => {
  notificationSequence += 1;

  return `dga-notification-${Date.now()}-${notificationSequence}`;
};

const applyDgaToastRtlFix = (
  toastElement
) => {
  if (!toastElement) {
    return;
  }

  const injectRtlFix = () => {
    const shadowRoot =
      toastElement.shadowRoot;

    if (
      !shadowRoot ||
      shadowRoot.querySelector(
        "#dga-toast-rtl-position-fix"
      )
    ) {
      return;
    }

    const styleElement =
      document.createElement("style");

    styleElement.id =
      "dga-toast-rtl-position-fix";

    styleElement.textContent = `
      .notification-toast__close-btn {
        right: auto !important;
        left: var(--spacing-3xl, 24px) !important;
      }

      .notification-toast__header {
        width: 100%;
        padding-inline-end: 44px !important;
      }

      @media only screen and (max-width: 37.5em) {
        .notification-toast__close-btn {
          right: auto !important;
          left: var(--spacing-xl, 16px) !important;
        }

        .notification-toast__header {
          padding-inline-end: 40px !important;
        }
      }
    `;

    shadowRoot.appendChild(
      styleElement
    );
  };

  if (
    typeof toastElement.componentOnReady ===
    "function"
  ) {
    toastElement
      .componentOnReady()
      .then(injectRtlFix);

    return;
  }

  window.requestAnimationFrame(
    injectRtlFix
  );
};

const publishNotification = ({
  type,
  title,
  message,
}) => {
  if (
    typeof window === "undefined"
  ) {
    return;
  }

  window.dispatchEvent(
    new CustomEvent(
      DGA_NOTIFICATION_EVENT,
      {
        detail: {
          id: createNotificationId(),
          type,
          title: String(
            title || ""
          ),
          message: String(
            message || ""
          ),
        },
      }
    )
  );
};

export const showDgaSuccess = (
  message,
  title = "تم بنجاح"
) => {
  publishNotification({
    type: "success",
    title,
    message,
  });
};

export const showDgaWarning = (
  message,
  title = "تنبيه"
) => {
  publishNotification({
    type: "warning",
    title,
    message,
  });
};

export const showDgaError = (
  message,
  title = "خطأ"
) => {
  publishNotification({
    type: "error",
    title,
    message,
  });
};

export const showDgaInfo = (
  message,
  title = "معلومة"
) => {
  publishNotification({
    type: "info",
    title,
    message,
  });
};

export const DgaToaster = () => {
  const [
    notification,
    setNotification,
  ] = useState(null);

  const enterFrameRef =
    useRef(null);

  const secondEnterFrameRef =
    useRef(null);

  /*
   * Controls automatic dismissal.
   */
  const autoDismissTimerRef =
    useRef(null);

  /*
   * Removes the notification after
   * its exit animation.
   */
  const closeTimerRef =
    useRef(null);

  const cancelScheduledWork =
    useCallback(() => {
      if (enterFrameRef.current) {
        window.cancelAnimationFrame(
          enterFrameRef.current
        );
      }

      if (
        secondEnterFrameRef.current
      ) {
        window.cancelAnimationFrame(
          secondEnterFrameRef.current
        );
      }

      if (
        autoDismissTimerRef.current
      ) {
        window.clearTimeout(
          autoDismissTimerRef.current
        );
      }

      if (closeTimerRef.current) {
        window.clearTimeout(
          closeTimerRef.current
        );
      }

      enterFrameRef.current = null;
      secondEnterFrameRef.current =
        null;
      autoDismissTimerRef.current =
        null;
      closeTimerRef.current = null;
    }, []);

  const handleClose =
    useCallback(() => {
      /*
       * Stop the automatic timer when
       * the user closes the toast manually
       * or when the timer itself fires.
       */
      if (
        autoDismissTimerRef.current
      ) {
        window.clearTimeout(
          autoDismissTimerRef.current
        );

        autoDismissTimerRef.current =
          null;
      }

      if (enterFrameRef.current) {
        window.cancelAnimationFrame(
          enterFrameRef.current
        );
      }

      if (
        secondEnterFrameRef.current
      ) {
        window.cancelAnimationFrame(
          secondEnterFrameRef.current
        );
      }

      enterFrameRef.current = null;
      secondEnterFrameRef.current =
        null;

      const reduceMotion =
        window.matchMedia?.(
          "(prefers-reduced-motion: reduce)"
        ).matches;

      if (reduceMotion) {
        setNotification(null);
        return;
      }

      /*
       * Start the official DGA toast
       * closing animation.
       */
      setNotification(
        (currentNotification) => {
          if (
            !currentNotification
          ) {
            return null;
          }

          return {
            ...currentNotification,
            open: false,
          };
        }
      );

      if (closeTimerRef.current) {
        window.clearTimeout(
          closeTimerRef.current
        );
      }

      /*
       * Remove it after the animation.
       */
      closeTimerRef.current =
        window.setTimeout(() => {
          setNotification(null);
          closeTimerRef.current =
            null;
        }, EXIT_ANIMATION_DURATION);
    }, []);

  useEffect(() => {
    const handleNotification = (
      event
    ) => {
      /*
       * Clear timers belonging to the
       * previous notification.
       */
      cancelScheduledWork();

      const nextNotification =
        event.detail;

      if (
        !nextNotification?.message
      ) {
        return;
      }

      const reduceMotion =
        window.matchMedia?.(
          "(prefers-reduced-motion: reduce)"
        ).matches;

      setNotification({
        ...nextNotification,
        open: reduceMotion,
      });

      /*
       * Central automatic dismissal.
       * This applies to success, warning,
       * error, and information toasts.
       */
      autoDismissTimerRef.current =
        window.setTimeout(() => {
          handleClose();
        }, AUTO_DISMISS_DURATION);

      if (reduceMotion) {
        return;
      }

      /*
       * Two animation frames ensure the
       * off-screen state is painted before
       * the official component receives
       * open=true.
       */
      enterFrameRef.current =
        window.requestAnimationFrame(
          () => {
            secondEnterFrameRef.current =
              window.requestAnimationFrame(
                () => {
                  setNotification(
                    (
                      currentNotification
                    ) => {
                      if (
                        currentNotification
                          ?.id !==
                        nextNotification.id
                      ) {
                        return currentNotification;
                      }

                      return {
                        ...currentNotification,
                        open: true,
                      };
                    }
                  );

                  enterFrameRef.current =
                    null;

                  secondEnterFrameRef.current =
                    null;
                }
              );
          }
        );
    };

    window.addEventListener(
      DGA_NOTIFICATION_EVENT,
      handleNotification
    );

    return () => {
      window.removeEventListener(
        DGA_NOTIFICATION_EVENT,
        handleNotification
      );

      cancelScheduledWork();
    };
  }, [
    cancelScheduledWork,
    handleClose,
  ]);

  if (!notification) {
    return null;
  }

  const isAssertive =
    notification.type === "error" ||
    notification.type === "warning";

  return (
    <div
      className="dga-notification-region"
      dir="rtl"
      role={
        isAssertive
          ? "alert"
          : "status"
      }
      aria-live={
        isAssertive
          ? "assertive"
          : "polite"
      }
      aria-atomic="true"
    >
      <DgaNotificationToast
        ref={applyDgaToastRtlFix}
        dir="rtl"
        key={notification.id}
        type={notification.type}
        leadText={
          notification.title
        }
        hasHelperText={Boolean(
          notification.message
        )}
        helperText={
          notification.message
        }
        closeButton
        action={false}
        vPostion="top"
        hPostion="right"
        open={notification.open}
        onClose={handleClose}
      />
    </div>
  );
};